import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CapaErrorCode,
  CapaQuotationChatRoom,
  CapaQuotationDetailedQuotation,
  CapaQuotationPartnerStrategy,
  CapaQuotationRfqDetail,
  CapaQuotationRfqPurchaseOrder,
  CapaQuotationUser,
  CapaRfqReview,
  useFetchMyQuotationUserLazyQuery,
  useFetchMyRfqQuotationsLazyQuery,
  useFetchPurchaseOrdersLazyQuery,
  useFetchQuotationChatRoomQuery,
  useFetchQuotationUserRfqDetailLazyQuery,
  useFetchQuotationUserRfqReviewsLazyQuery,
  useFetchWithdrawnQuotationPartnerLazyQuery
} from '@generated/graphql';
import { RootState } from '@modules/index';

interface ProjectDetails {
  chatroom?: CapaQuotationChatRoom;
  myInfo?: CapaQuotationUser;
  partnerInfo?: Omit<CapaQuotationPartnerStrategy, 'email'>;
  rfqInfo?: CapaQuotationRfqDetail;
  quotations?: CapaQuotationDetailedQuotation[];
  purchaseOrders?: CapaQuotationRfqPurchaseOrder[];
  reviews?: CapaRfqReview[];
}

type ProjectDetailWatches = (keyof Omit<ProjectDetails, 'chatroom'>)[];

interface UseProject {
  chatRoomId?: string;
  rfqId?: string;
  quotationPartnerId?: string;
  watches: ProjectDetailWatches;
}

const useProjectDetails = ({
  chatRoomId = '',
  rfqId = '',
  quotationPartnerId = '',
  watches
}: UseProject) => {
  const [input, setInput] = useState<{
    rfqId: string;
    quotationPartnerId: string;
  }>({
    rfqId,
    quotationPartnerId
  });
  const watchesRef = useRef<ProjectDetailWatches>(watches);
  const myId = useSelector<RootState, string>((store) => store.client.id ?? '');

  const { data: chatRoomData } = useFetchQuotationChatRoomQuery({
    variables: {
      input: {
        chatRoomId
      }
    },
    skip: !chatRoomId,
    onCompleted: (result) => {
      const isSuccess =
        result.fetchQuotationChatRoom.result === CapaErrorCode.Success;
      if (isSuccess) {
        setInput({
          rfqId: result.fetchQuotationChatRoom.data?.rfqId ?? '',
          quotationPartnerId:
            result.fetchQuotationChatRoom.data?.quotationPartnerId ?? ''
        });
      }
    }
  });

  const [
    fetchPartnerInfo,
    { data: partnerInfo }
  ] = useFetchWithdrawnQuotationPartnerLazyQuery();

  const [fetchMyInfo, { data: myInfo }] = useFetchMyQuotationUserLazyQuery();

  const [
    fetchRfq,
    { data: rfqInfo }
  ] = useFetchQuotationUserRfqDetailLazyQuery();

  const [
    fetchQuotations,
    { data: quotations }
  ] = useFetchMyRfqQuotationsLazyQuery();

  const [
    fetchPurchaseOrders,
    { data: purchaseOrders }
  ] = useFetchPurchaseOrdersLazyQuery();

  const [
    fetchReviews,
    { data: reviews }
  ] = useFetchQuotationUserRfqReviewsLazyQuery();

  const watchToLazyFetchMap: Record<
    keyof Omit<ProjectDetails, 'chatroom'>,
    () => void
  > = useMemo(
    () => ({
      myInfo: fetchMyInfo,
      partnerInfo: () => {
        if (!input.quotationPartnerId) return;
        fetchPartnerInfo({
          variables: { quotationPartnerId: input.quotationPartnerId }
        });
      },
      purchaseOrders: () => {
        if (!input.quotationPartnerId || !input.rfqId) return;
        fetchPurchaseOrders({
          variables: {
            input
          }
        });
      },
      quotations: () => {
        if (!input.quotationPartnerId || !input.rfqId) return;
        fetchQuotations({
          variables: {
            input
          }
        });
      },
      reviews: () =>
        fetchReviews({
          variables: {
            input: {
              quotationUserId: myId
            }
          }
        }),
      rfqInfo: () => {
        if (!input.rfqId) return;
        fetchRfq({
          variables: {
            rfqId: input.rfqId
          }
        });
      }
    }),
    //eslint-disable-next-line
    [input.rfqId, input.quotationPartnerId]
  );

  useEffect(() => {
    for (const watch of watchesRef.current) {
      watchToLazyFetchMap[watch]();
    }
  }, [watchToLazyFetchMap]);

  const projectDetails = useMemo(() => {
    return {
      chatroom: chatRoomData?.fetchQuotationChatRoom?.data,
      myInfo: myInfo?.fetchMyQuotationUser?.data,
      partnerInfo: partnerInfo?.fetchWithdrawnQuotationPartner.data,
      rfqInfo: rfqInfo?.fetchQuotationUserRfqDetail?.data,
      quotations: quotations?.fetchMyRfqQuotations?.data,
      purchaseOrders: purchaseOrders?.fetchPurchaseOrders?.data,
      paymentDatas:
        purchaseOrders?.fetchPurchaseOrders?.data?.[0].paymentDataList,
      shippingDatas:
        purchaseOrders?.fetchPurchaseOrders?.data?.[0].shippingDataList,
      reviews: reviews?.fetchQuotationUserRfqReviews?.data
    };
  }, [
    chatRoomData,
    myInfo,
    partnerInfo,
    rfqInfo,
    quotations,
    purchaseOrders,
    reviews
  ]);

  return projectDetails;
};

export default useProjectDetails;
